import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * The discount-code mark a product carries — a dashed badge stating what the
 * code takes off, the price it leaves, and the code itself.
 *
 *   <ProductCouponMark>
 *     <ProductCouponAmounts>
 *       <ProductCouponSaving>&#8208;15 %</ProductCouponSaving>
 *       <ProductCouponPrice>1 290 Kč</ProductCouponPrice>
 *     </ProductCouponAmounts>
 *     <ProductCouponCode variant={ProductCouponCodeVariant.PLAIN}>
 *       Kód: <b>LETO15</b>
 *     </ProductCouponCode>
 *   </ProductCouponMark>
 *
 * Two faces of one mark: {@link ProductCouponMark} states the offer, and
 * {@link ProductCouponAction} is the same face as a control that spends it.
 * They paint identically — the face is one class constant both render — because
 * the shopper is meant to recognise the same badge whether or not it is
 * clickable.
 *
 * Its own module rather than a part of the product card, because the card is
 * only one of its two mounts: the PDP's buy box hangs the same mark under the
 * add-to-cart row, at a different width and with the code line reading
 * differently. A copy per section would drift between them.
 *
 * Everything that computes the mark stays tenant-side: the percentage, the
 * money and the currency are formatted app-side and arrive as children, and
 * which face renders is a decision over the tenant's own product data.
 */

/**
 * The container-query context both faces step against, and the frame that
 * establishes it.
 *
 * Rendered by the part rather than left to a composition: the `@[300px]` steps
 * below resolve against the nearest container, so a composition that omitted
 * this would still render the mark, with every step silently compiling to
 * nothing against whatever container happened to be further up the tree.
 */
const productCouponFrameClasses = '@container';

/**
 * The stating face's frame additionally claims its column.
 *
 * rcprofi's value verbatim, and the one place the two faces differ: the acting
 * face is a `<button>` that stretches itself, while the stating one is a `<div>`
 * whose width is its parent's to decide — which in a flex row is the difference
 * between a badge across the strip and a badge around its own copy.
 */
const productCouponMarkFrameClasses = clsxm(productCouponFrameClasses, 'w-full');

/**
 * The face, painted the same for both mounts. rcprofi's values verbatim: the
 * dashed warning border over the discount tint is what a shopper reads as "a
 * code applies here", and another tenant overrides it at its own call site.
 */
const productCouponFaceClasses = clsxm(
  'flex w-full items-center justify-between gap-1 rounded-lg px-2 py-1',
  'border-warning/60 bg-discount-banner/60 text-foreground-muted border border-dashed',
  'text-xs tracking-tighter @[300px]:text-sm'
);

export type ProductCouponMarkProps = ComponentProps<'div'>;

/** The mark as a statement: the offer, not a control. */
export const ProductCouponMark = ({ className, ...props }: ProductCouponMarkProps) => (
  <div data-slot="product-coupon" className={productCouponMarkFrameClasses}>
    <div
      data-slot="product-coupon-mark"
      className={clsxm(productCouponFaceClasses, className)}
      {...props}
    />
  </div>
);

export type ProductCouponActionProps = ComponentProps<'button'>;

/**
 * The mark as a control: the same face, spending the code on click.
 *
 * A `<button>` and not a link — nothing is navigated to, the code is applied to
 * the order in place. The pending state is composed rather than configured:
 * a tenant swaps the contents for a {@link ProductCouponPending}, so the
 * spinner it uses stays its own dependency.
 */
export const ProductCouponAction = ({ className, ...props }: ProductCouponActionProps) => (
  <div data-slot="product-coupon" className={productCouponFrameClasses}>
    <button
      data-slot="product-coupon-action"
      className={clsxm(productCouponFaceClasses, className)}
      {...props}
    />
  </div>
);

/** The leading end of the face: what the code takes off, beside what it leaves. */
const productCouponAmountsClasses = 'flex items-center gap-x-2';

export type ProductCouponAmountsProps = ComponentProps<'div'>;

/** The saving and the price, as one group. */
export const ProductCouponAmounts = ({ className, ...props }: ProductCouponAmountsProps) => (
  <div
    data-slot="product-coupon-amounts"
    className={clsxm(productCouponAmountsClasses, className)}
    {...props}
  />
);

/** The chip stating the saving. Its copy — sign, number and unit — is the tenant's. */
const productCouponSavingClasses =
  'rounded-sm bg-warning-intense px-1 font-semibold text-foreground-inverted';

export type ProductCouponSavingProps = ComponentProps<'div'>;

/** The percentage chip. */
export const ProductCouponSaving = ({ className, ...props }: ProductCouponSavingProps) => (
  <div
    data-slot="product-coupon-saving"
    className={clsxm(productCouponSavingClasses, className)}
    {...props}
  />
);

/** The price the code leaves. Steps up with the container, like the code line. */
const productCouponPriceClasses = 'text-sm font-semibold @[300px]:text-base';

export type ProductCouponPriceProps = ComponentProps<'span'>;

/** The discounted price. */
export const ProductCouponPrice = ({ className, ...props }: ProductCouponPriceProps) => (
  <span
    data-slot="product-coupon-price"
    className={clsxm(productCouponPriceClasses, className)}
    {...props}
  />
);

/**
 * How the code line reads.
 *
 * The one appearance axis this mark has, and it is genuinely appearance: the
 * same sentence is underlined where clicking it does something and plain where
 * it is a statement. What the sentence SAYS — "Kód: X" against "Koupit s kódem
 * X", and whether the code name is spelled out at all — is copy, so it is
 * composed as children rather than selected by a prop.
 */
export const productCouponCodeVariants = cva('text-xs whitespace-nowrap @[300px]:text-base', {
  variants: {
    variant: {
      /**
       * A statement of the code. Paints nothing over the base, which is also
       * what a tenant painting the line itself asks for — so there is no
       * separate `unstyled` arm here: a second empty member would be two names
       * for one look, and no call site could tell them apart.
       */
      plain: '',
      /** The code as something to act on. */
      underlined: 'underline underline-offset-2',
    },
  },
});

type ProductCouponCodeVariantName = NonNullable<
  VariantProps<typeof productCouponCodeVariants>['variant']
>;

export type ProductCouponCodeVariant = ProductCouponCodeVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductCouponCodeVariant = {
  PLAIN: 'plain',
  UNDERLINED: 'underlined',
} as const satisfies Record<string, ProductCouponCodeVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductCouponCodeVariantProps = { readonly variant: ProductCouponCodeVariant };

export type ProductCouponCodeProps = ComponentProps<'div'> & ProductCouponCodeVariantProps;

/** The code line at the trailing end of the face. */
export const ProductCouponCode = ({ className, variant, ...props }: ProductCouponCodeProps) => (
  <div
    data-slot="product-coupon-code"
    className={clsxm(productCouponCodeVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The box the face's contents give way to while the code is being applied.
 *
 * It holds the height the face has when it is stating an offer, so the mark
 * does not collapse under its own spinner and shift everything below it. What
 * spins inside is the tenant's — this package names no spinner.
 */
const productCouponPendingClasses = 'flex h-5 w-full items-center justify-center @[300px]:h-[25px]';

export type ProductCouponPendingProps = ComponentProps<'div'>;

/** The pending face's contents. */
export const ProductCouponPending = ({ className, ...props }: ProductCouponPendingProps) => (
  <div
    data-slot="product-coupon-pending"
    className={clsxm(productCouponPendingClasses, className)}
    {...props}
  />
);
